import { forwardRef, ForwardRefExoticComponent, RefAttributes, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Avatar, Badge, Chip, ListItemButton, ListItemIcon, ListItemText, Tooltip, Typography } from '@mui/material';

// project import
import { activeItem } from 'store/reducers/menu';
import { useUpgradeDrawer } from '../../../../../contexts/UpgradeDrawerContext';

// types
import { LinkTarget, NavItemType } from 'types/menu';
import { RootStateProps } from 'types/root';
import { textSizeByBreakpoint } from '../../../../../utils/helper';

// ==============================|| NAVIGATION - LIST ITEM ||============================== //

interface Props {
  item: NavItemType;
}

const NavItem = ({ item }: Props) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const menu = useSelector((state: RootStateProps) => state.menu);
  const { drawerOpen } = menu;
  const { openUpgradeDrawer } = useUpgradeDrawer();

  let itemTarget: LinkTarget = '_self';
  if (item.target) {
    itemTarget = '_blank';
  }

  let listItemProps: {
    component: ForwardRefExoticComponent<RefAttributes<HTMLAnchorElement>> | string;
    href?: string;
    target?: LinkTarget;
  } = { component: forwardRef((props, ref) => <Link {...props} to={item.url!} target={itemTarget} />) };
  if (item?.external) {
    listItemProps = { component: 'a', href: item.url, target: itemTarget };
  }

  const Icon = item.icon!;
  const itemIcon = item.icon ? <Icon sx={{ color: 'inherit' }} /> : false;

  // let isSelected = openItem.findIndex((id) => id === item.id) > -1;

  const [isSelected, setIsSelected] = useState(false);

  const { pathname } = useLocation();

  useEffect(() => {
    const selected = pathname?.includes(item.url || '');
    setIsSelected(selected);
  }, [pathname]);

  // active menu item on page load
  useEffect(() => {
    if (pathname && pathname.includes('product-details')) {
      if (item.url && item.url.includes('product-details')) {
        dispatch(activeItem({ openItem: [item.id] }));
      }
    }

    if (pathname === item.url) {
      dispatch(activeItem({ openItem: [item.id] }));
    }
    // eslint-disable-next-line
  }, [pathname]);
  const [frm, setfrm] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const textColor = theme.palette.mode === 'dark' ? 'grey.400' : 'navItem.main';
  const iconSelectedColor = 'primary.main';
  const disabledColor = theme.palette.mode === 'dark' ? 'grey.600' : 'grey.400';

  // Check if item is locked for free users or analyzer
  const isLocked = item.isAnalyzerLocked || item.isFreeUserLocked;

  const handleOnClick = (e: any, item: any) => {
    if (item.isFreeUserLocked) {
      e.preventDefault();
      setTooltipOpen(false); // Close tooltip when opening upgrade drawer
      openUpgradeDrawer('copier');
      return;
    }
    if (item.isAnalyzerLocked) {
      e.preventDefault();
      setTooltipOpen(false); // Close tooltip when opening upgrade drawer
      openUpgradeDrawer('analytics');
      return;
    }
    if (item.isFeatureRequest || item.disabled) {
      e.preventDefault();
      if (item.isFeatureRequest) {
        console.log('FeatureRequestModal');
        setfrm(!frm);
      }
    }
  };

  return (
    <>
      {/*<FeatureRequestModal open={frm} handleClose={() => setfrm(false)} />*/}
      <Tooltip
        placement={'right'}
        title={item.tooltip || (!drawerOpen ? item.title : '')}
        disableHoverListener={!item.tooltip && drawerOpen}
        followCursor={drawerOpen && !!item.tooltip}
        open={isLocked ? tooltipOpen : undefined}
        onOpen={() => isLocked && setTooltipOpen(true)}
        onClose={() => isLocked && setTooltipOpen(false)}
      >
        <span style={{ display: 'block', cursor: isLocked ? 'pointer' : (item.disabled ? 'not-allowed' : 'inherit') }}>
          <ListItemButton
            {...listItemProps}
            disabled={item.disabled && !isLocked}
            selected={isSelected}
            onClick={(e) => {
              handleOnClick(e, item);
            }}
            sx={{
              pointerEvents: isLocked ? 'auto' : (item.disabled ? 'none' : 'auto'),
              zIndex: 1201,
              pl: drawerOpen ? '10px' : 1.5,
              py: '5px',
              mx: drawerOpen ? '20px' : 0, // All items have 20px margin (inside the 30px sidebar padding)
              borderRadius: drawerOpen ? 2 : 0,
              border: '1px solid transparent',
              ...(drawerOpen && !isLocked && {
                '&:hover': {
                  border: `1px solid ${theme.palette.border.main}`,
                  boxShadow: `0 3px 4px 0 rgba(0, 0, 0, 0.05)`,
                  bgcolor: theme.palette.background.paper,
                  borderRadius: 2
                },
                '&.Mui-selected': {
                  border: `1px solid ${theme.palette.border.main}`,
                  boxShadow: `0 3px 4px 0 rgba(0, 0, 0, 0.05)`,
                  bgcolor: theme.palette.background.paper,
                  color: iconSelectedColor,
                  borderRadius: 2,
                  '&:hover': {
                    border: `1px solid ${theme.palette.border.main}`,
                    boxShadow: `0 3px 4px 0 rgba(0, 0, 0, 0.05)`,
                    color: iconSelectedColor,
                    bgcolor: theme.palette.background.paper
                  }
                }
              }),
              ...(drawerOpen && isLocked && {
                '&:hover': {
                  bgcolor: 'transparent',
                  border: '1px solid transparent'
                }
              }),
              ...(!drawerOpen && {
                '&:hover': {
                  bgcolor: 'transparent'
                },
                '&.Mui-selected': {
                  '&:hover': {
                    bgcolor: 'transparent'
                  },
                  bgcolor: 'transparent'
                }
              })
            }}
          >
            {itemIcon && !drawerOpen && (
              <ListItemIcon
                sx={{
                  minWidth: 16,
                  marginRight: 1,
                  color: isLocked ? disabledColor : isSelected ? iconSelectedColor : textColor,
                  opacity: isLocked ? 0.5 : 1,
                  ...(!drawerOpen && {
                    borderRadius: 1.5,
                    width: 36,
                    height: 36,
                    alignItems: 'center',
                    justifyContent: 'center',
                    '&:hover': {
                      bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'secondary.lighter',
                      color: 'primary.main'
                    }
                  }),
                  ...(!drawerOpen &&
                    isSelected && {
                      bgcolor: theme.palette.mode === 'dark' ? 'primary.900' : 'primary.lighter',
                      '&:hover': {
                        bgcolor: theme.palette.mode === 'dark' ? 'primary.darker' : 'primary.lighter'
                      }
                    })
                }}
              >
                {item.badgeValue && (
                  <Badge color={item.badgeColor} badgeContent={item.badgeValue}>
                    {itemIcon}
                  </Badge>
                )}
                {!item.badgeValue && itemIcon}
              </ListItemIcon>
            )}
            {itemIcon && drawerOpen && (
              <ListItemIcon
                sx={{
                  minWidth: 20,
                  marginRight: 1,
                  color: isLocked ? disabledColor : isSelected ? iconSelectedColor : textColor,
                  opacity: isLocked ? 0.5 : 1
                }}
              >
                {item.badgeValue && (
                  <Badge color={item.badgeColor} badgeContent={item.badgeValue}>
                    {itemIcon}
                  </Badge>
                )}
                {!item.badgeValue && itemIcon}
              </ListItemIcon>
            )}
            {drawerOpen && (
              <ListItemText
                primary={
                  <Typography
                    variant="h6"
                    sx={{
                      color: isLocked ? disabledColor : isSelected ? iconSelectedColor : textColor,
                      fontSize: textSizeByBreakpoint(15),
                      fontWeight: isSelected ? 700 : 500,
                      letterSpacing: '-0.02em',
                      opacity: isLocked ? 0.5 : 1
                    }}
                  >
                    {item.title}
                  </Typography>
                }
              />
            )}
            {drawerOpen && item.chip && (
              <Chip
                color={item.chip.color}
                variant={item.chip.variant}
                size={item.chip.size}
                label={item.chip.label}
                avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
              />
            )}
          </ListItemButton>
        </span>
      </Tooltip>
    </>
  );
};

export default NavItem;
